import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptPartnerOrder,
  assignOeloxDriver,
  assignPartnerDriver,
  blastPartnerDrivers,
  declinePartnerOrder,
  fetchPartnerDriverOrders,
  getPartnerFallbackEnabled,
  getPartnerOrder,
  listPartnerOeloxDrivers,
  listPartnerOrders,
  requestOeloxDriver,
  unassignPartnerDriver,
} from "../lib/partnerOrders";
import { listPartnerDrivers } from "../lib/partnerDrivers";
import { usePartnerSessionStore } from "../lib/partnerAuth";

export function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

export function usePartnerOrdersList() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-orders", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerOrders(partnerId!),
    refetchInterval: 12_000,
  });
}

export function usePartnerOrderDetail(orderId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-order", partnerId, orderId],
    enabled: !!partnerId && !!orderId,
    queryFn: () => getPartnerOrder(partnerId!, orderId!),
    // Keep the open order live — mutations happen on the phone as well.
    refetchInterval: 12_000,
  });
}

export function usePartnerDriverOrders(
  driverId: string | null,
  opts: { page: number; pageSize: number }
) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: [
      "partner-driver-orders",
      partnerId,
      driverId,
      opts.page,
      opts.pageSize,
    ],
    enabled: !!partnerId && !!driverId,
    queryFn: () =>
      fetchPartnerDriverOrders(partnerId!, driverId!, {
        limit: opts.pageSize,
        offset: opts.page * opts.pageSize,
      }),
  });
}

export function usePartnerDrivers() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-drivers", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerDrivers(partnerId!),
    refetchInterval: 15_000,
  });
}

export function usePartnerFallbackEnabled() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-fallback", partnerId],
    enabled: !!partnerId,
    queryFn: () => getPartnerFallbackEnabled(partnerId!),
    staleTime: 60_000,
  });
}

function useInvalidateOrders() {
  const qc = useQueryClient();
  const partnerId = usePartnerId();
  return () => {
    void qc.invalidateQueries({ queryKey: ["partner-orders", partnerId] });
    void qc.invalidateQueries({ queryKey: ["partner-order", partnerId] });
    void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
  };
}

export function useAcceptPartnerOrder() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: { tenantId: string; orderId: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return acceptPartnerOrder({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
      });
    },
    onSuccess: invalidate,
  });
}

/** Phase 2: decline a routed order BEFORE accepting it. */
export function useDeclinePartnerOrder() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: { tenantId: string; orderId: string; reason?: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return declinePartnerOrder({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
        reason: input.reason,
      });
    },
    onSuccess: invalidate,
  });
}

export function useAssignPartnerDriver() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: {
      tenantId: string;
      orderId: string;
      driverId: string;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return assignPartnerDriver({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
        driverId: input.driverId,
      });
    },
    onSuccess: invalidate,
  });
}

export function usePartnerOeloxDrivers(enabled: boolean) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-oelox-drivers", partnerId],
    enabled: !!partnerId && enabled,
    queryFn: () => listPartnerOeloxDrivers(partnerId!),
    refetchInterval: 20_000,
  });
}

export function useAssignPartnerOeloxDriver() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: {
      tenantId: string;
      orderId: string;
      driverId: string;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return assignOeloxDriver({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
        driverId: input.driverId,
      });
    },
    onSuccess: invalidate,
  });
}

export function useBlastPartnerDrivers() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: {
      tenantId: string;
      orderId: string;
      driverIds?: string[];
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return blastPartnerDrivers({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
        driverIds: input.driverIds,
      });
    },
    onSuccess: invalidate,
  });
}

export function useUnassignPartnerDriver() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: { tenantId: string; orderId: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return unassignPartnerDriver({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
      });
    },
    onSuccess: invalidate,
  });
}

export function useRequestOeloxDriver() {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (input: { tenantId: string; orderId: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return requestOeloxDriver({
        partnerId,
        tenantId: input.tenantId,
        orderId: input.orderId,
      });
    },
    onSuccess: invalidate,
  });
}
